import type { SupabaseClient } from '../../lib/supabase';
import type { BareAudioMime } from './audioFormat';

/**
 * The browser side of the huddle rpcs (h1-plan.md section 1, h1h2-plan.md
 * 1.3 and 1.8). Lane 1 writes the SQL; until its regenerated types land these
 * functions are not in src/types/database.types.ts, so the calls go through a
 * narrow untyped door and every answer is parsed, never trusted (D4 in
 * sprint-allout.md). At the stack gate the door can be swapped for the
 * generated types without touching a caller.
 */

export const HUDDLE_MAX = 12;

/** One active seat, as join_huddle's peers and huddle_heartbeat's roster return it. */
export interface HuddleSeat {
  userId: string;
  sessionId: string;
  name: string;
}

export interface JoinResult {
  huddleId: string;
  participantId: string;
  max: number;
  peers: HuddleSeat[];
  /** Milliseconds between the huddle's start and the server's now at join (plan 2.3). */
  clockBaseMs: number;
  startedAt: string | null;
}

export interface LiveHuddle {
  projectId: string;
  huddleId: string;
  activeCount: number;
}

interface RpcAnswer {
  data: unknown;
  error: { message: string } | null;
  status?: number;
}

type LooseRpc = (fn: string, args?: Record<string, unknown>) => PromiseLike<RpcAnswer>;

/**
 * The untyped door. Everything that crosses it is parsed below. The call keeps
 * `this` bound to the client (supabase-js reads this.rest inside rpc).
 */
export function looseRpc(client: SupabaseClient): LooseRpc {
  const target = client as unknown as { rpc: LooseRpc };
  return (fn, args) => target.rpc(fn, args);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

export function parseSeats(value: unknown): HuddleSeat[] {
  if (!Array.isArray(value)) return [];
  const seats: HuddleSeat[] = [];
  for (const item of value) {
    const row = asRecord(item);
    if (row === null) continue;
    const userId = text(row.user_id);
    const sessionId = text(row.session_id);
    if (userId === '' || sessionId === '') continue;
    const name = text(row.name) || text(row.display_name);
    seats.push({ userId, sessionId, name });
  }
  return seats;
}

export function parseJoinResult(data: unknown): JoinResult {
  const row = asRecord(data);
  const huddleId = text(row?.huddle_id);
  const participantId = text(row?.participant_id);
  if (row === null || huddleId === '' || participantId === '') {
    throw new Error('huddle_bad_answer');
  }
  const max = typeof row.max === 'number' && row.max > 0 ? row.max : HUDDLE_MAX;
  const startedAt = text(row.started_at) || null;
  const serverNow = Date.parse(text(row.server_now));
  const started = startedAt === null ? Number.NaN : Date.parse(startedAt);
  const clockBaseMs =
    Number.isFinite(serverNow) && Number.isFinite(started) ? Math.max(0, serverNow - started) : 0;
  return { huddleId, participantId, max, peers: parseSeats(row.peers), clockBaseMs, startedAt };
}

export function parseLiveHuddles(data: unknown): LiveHuddle[] {
  if (!Array.isArray(data)) return [];
  const out: LiveHuddle[] = [];
  for (const item of data) {
    const row = asRecord(item);
    if (row === null) continue;
    const projectId = text(row.project_id);
    const huddleId = text(row.huddle_id);
    if (projectId === '' || huddleId === '') continue;
    const activeCount = typeof row.active_count === 'number' ? row.active_count : 0;
    out.push({ projectId, huddleId, activeCount });
  }
  return out;
}

/** An rpc answer as an Error carrying the http status, so callers can sort network from refusal. */
export class HuddleRpcError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'HuddleRpcError';
    this.status = status;
  }
}

async function call(client: SupabaseClient, fn: string, args?: Record<string, unknown>) {
  const answer = await looseRpc(client)(fn, args);
  if (answer.error) {
    throw new HuddleRpcError(answer.error.message, answer.status ?? 0);
  }
  return answer.data;
}

export async function joinHuddle(
  client: SupabaseClient,
  roomId: string,
  sessionId: string,
): Promise<JoinResult> {
  return parseJoinResult(
    await call(client, 'join_huddle', { p_room: roomId, p_session: sessionId }),
  );
}

export async function leaveHuddle(client: SupabaseClient, participantId: string): Promise<void> {
  await call(client, 'leave_huddle', { p_participant: participantId });
}

/** Returns the current roster; raises not_in_huddle when the seat is gone. */
export async function huddleHeartbeat(
  client: SupabaseClient,
  participantId: string,
): Promise<HuddleSeat[]> {
  const data = await call(client, 'huddle_heartbeat', { p_participant: participantId });
  return parseSeats(asRecord(data)?.roster);
}

export async function myLiveHuddles(client: SupabaseClient): Promise<LiveHuddle[]> {
  return parseLiveHuddles(await call(client, 'my_live_huddles'));
}

/** A missing migration, refused request or network error keeps calls hidden. */
export async function huddlesAvailable(client: SupabaseClient): Promise<boolean> {
  try {
    return (await call(client, 'huddles_available')) === true;
  } catch {
    return false;
  }
}

export interface ChunkRecord {
  participantId: string;
  seq: number;
  offsetMs: number;
  durationMs: number;
  bytes: number;
  mime: BareAudioMime;
}

export async function recordHuddleChunk(client: SupabaseClient, chunk: ChunkRecord): Promise<void> {
  await call(client, 'record_huddle_chunk', {
    p_participant: chunk.participantId,
    p_seq: chunk.seq,
    p_offset_ms: chunk.offsetMs,
    p_duration_ms: chunk.durationMs,
    p_bytes: chunk.bytes,
    p_mime: chunk.mime,
  });
}

/**
 * Plain words for the rpc errors (h1-plan.md:212). Matched on message text,
 * never on SQLSTATE (traps.md:33).
 */
export function huddleErrorMessage(message: string, max: number = HUDDLE_MAX): string {
  if (message.includes('huddle_full')) return `This huddle is full (${max} max)`;
  if (message.includes('huddle_not_allowed'))
    return 'Only people in this room can join its huddle.';
  if (message.includes('not_in_huddle')) return 'You joined this huddle in another tab.';
  // A bare code or nothing at all (a network drop) reads as one plain line.
  if (message.trim() === '' || /^[a-z0-9_]+$/.test(message.trim())) {
    return 'The huddle could not be joined. Try again.';
  }
  return message;
}

/** The exact notice lines (h1h2-plan.md 0.2, 2.6, 2.7; D6). */
export const HUDDLE_TEXT = {
  beforeJoin:
    'This huddle records your microphone. Saved audio and a best-effort transcript are posted in this room after you leave. Upload interruptions may leave gaps.',
  inCall: 'Recording and transcribing',
  notRecorded: 'Your voice is not being recorded in this browser.',
  paused: 'Recording paused: connection lost',
  saving: 'Saving your audio...',
  micDenied:
    "The Hub can't use your microphone. Allow it in the address bar (the camera/mic icon), then Join again.",
  noMic: 'No microphone found.',
  connectionLost: 'Connection lost. Rejoin',
} as const;
